<template>
  <div class="pcd-management-root">
    <div class="management-card">
      <h2 class="card-title">PCD 合約管理</h2>
      
      <!-- 私募投資者管理區域 -->
      <div class="management-section">
        <div class="section-header">
          <h3>私募投資者管理</h3>
        </div>
        <div class="section-content">
          <div class="input-row">
            <input 
              type="text" 
              v-model="newPrivateInvestor" 
              class="address-input"
              placeholder="請輸入私募投資者地址"
            />
            <button 
              class="update-btn" 
              @click="handleSetPrivateInvestor(true)"
              :disabled="isUpdatingPrivateInvestor"
            >
              {{ isUpdatingPrivateInvestor ? '更新中...' : '添加私募投資者' }}
            </button>
            <button 
              class="update-btn danger" 
              @click="handleSetPrivateInvestor(false)"
              :disabled="isUpdatingPrivateInvestor"
            >
              {{ isUpdatingPrivateInvestor ? '更新中...' : '移除私募投資者' }}
            </button>
          </div>
        </div>
      </div>

      <!-- 私募投資區域 -->
      <div class="management-section">
        <div class="section-header">
          <h3>私募投資</h3>
        </div>
        <div class="section-content">
          <div class="info-row">
            <span class="info-label">當前 PCD 價格：</span>
            <span class="info-value">{{ pcdPrice }} USDT</span>
          </div>
          <div class="info-row">
            <span class="info-label">私募價格：</span>
            <span class="info-value">{{ privatePrice }} USDT</span>
          </div>
          <div class="info-row">
            <span class="info-label">USDT 授權數量：</span>
            <span class="info-value">{{ usdtAllowance }} USDT</span>
          </div>
          <div class="input-row">
            <input 
              type="number" 
              v-model="privateInvestAmount" 
              class="count-input"
              placeholder="請輸入投資 USDT 數量"
              min="0"
              step="0.01"
            />
            <button 
              class="update-btn" 
              @click="handleApproveUsdt"
              :disabled="isPrivateInvesting || isCheckingAllowance"
            >
              {{ isPrivateInvesting ? '授權中...' : '授權 USDT' }}
            </button>
            <button 
              class="update-btn" 
              @click="handlePrivateInvest"
              :disabled="isPrivateInvesting || isCheckingAllowance"
            >
              {{ isPrivateInvesting ? '投資中...' : '確認投資' }}
            </button>
          </div>
        </div>
      </div>

      <!-- PCD 鑄造區域 -->
      <div class="management-section">
        <div class="section-header">
          <h3>PCD 鑄造</h3>
        </div>
        <div class="section-content">
          <div class="input-row">
            <input 
              type="text" 
              v-model="mintToAddress" 
              class="address-input"
              placeholder="請輸入接收地址"
            />
          </div>
          <div class="input-row">
            <input 
              type="number" 
              v-model="mintAmount" 
              class="count-input"
              placeholder="請輸入鑄造數量"
              min="1"
              max="20000"
            />
            <button 
              class="update-btn" 
              @click="handleMintPCD"
              :disabled="isMinting"
            >
              {{ isMinting ? '鑄造中...' : '鑄造 PCD' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { notify } from '@/composables/swalConfig';
import { 
  setPrivateInvestor, 
  privateInvest,
  mintPCD
} from '@/composables/contractPcdService';
import { useWalletStore } from '@/stores/walletStore';
import { getUSDTBalance, approveUSDT, getUSDTAllowance } from '@/composables/contractUsdtService';
import { useWalletAccount } from '@/composables/useWalletAccount';
import addresses from '@/composables/addresses';

const accountData = useWalletAccount();


const walletStore = useWalletStore();

// 私募投資者管理相關
const newPrivateInvestor = ref('');
const isUpdatingPrivateInvestor = ref(false);

// 私募投資相關
const pcdPrice = computed(() => walletStore.pcdPrice);
const privatePrice = computed(() => (Number(pcdPrice.value) * 0.5).toFixed(6));
const privateInvestAmount = ref('');
const isPrivateInvesting = ref(false);

// 添加 USDT 授權相關的狀態
const usdtAllowance = ref('0');
const isCheckingAllowance = ref(false);

// PCD 鑄造相關
const mintToAddress = ref('');
const mintAmount = ref('');
const isMinting = ref(false);

// 設置私募投資者
const handleSetPrivateInvestor = async (enabled) => {
  if (!newPrivateInvestor.value) {
    notify.error('請輸入有效的地址');
    return;
  }

  try {
    isUpdatingPrivateInvestor.value = true;
    await setPrivateInvestor(newPrivateInvestor.value, enabled);
    notify.success(enabled ? '添加私募投資者成功' : '移除私募投資者成功');
    newPrivateInvestor.value = '';
  } catch (error) {
    console.error('設置私募投資者失敗:', error);
    notify.error('操作失敗：' + (error.message || '未知錯誤'));
  } finally {
    isUpdatingPrivateInvestor.value = false;
  }
};

// 檢查 USDT 授權數量
const checkUsdtAllowance = async () => {
  if (!accountData.value) {
    notify.error('請先連接錢包');
    return false;
  }

  try {
    isCheckingAllowance.value = true;
    // 使用 getUSDTAllowance 查詢授權數量
    const allowance = await getUSDTAllowance(accountData.value.address, addresses.pcdContractAddress);
    usdtAllowance.value = allowance;
    return Number(usdtAllowance.value) >= Number(privateInvestAmount.value);
  } catch (error) {
    console.error('檢查 USDT 授權數量失敗:', error);
    notify.error('檢查授權數量失敗：' + (error.message || '未知錯誤'));
    return false;
  } finally {
    isCheckingAllowance.value = false;
  }
};

// 處理 USDT 授權
const handleApproveUsdt = async () => {
  if (!privateInvestAmount.value || Number(privateInvestAmount.value) <= 0) {
    notify.error('請輸入有效的投資金額');
    return;
  }

  try {
    isPrivateInvesting.value = true;
    await approveUSDT(addresses.pcdContractAddress, privateInvestAmount.value);
    notify.success('USDT 授權成功');
    // 重新檢查授權數量
    await checkUsdtAllowance();
  } catch (error) {
    console.error('USDT 授權失敗:', error);
    notify.error('授權失敗：' + (error.message || '未知錯誤'));
  } finally {
    isPrivateInvesting.value = false;
  }
};

// 修改私募投資處理函數
const handlePrivateInvest = async () => {
  if (!privateInvestAmount.value || Number(privateInvestAmount.value) <= 0) {
    notify.error('請輸入有效的投資金額');
    return;
  }

  try {
    isPrivateInvesting.value = true;
    
    // 檢查 USDT 授權數量
    const hasEnoughAllowance = await checkUsdtAllowance();
    if (!hasEnoughAllowance) {
      notify.warning('USDT 授權數量不足，請先授權');
      return;
    }

    await privateInvest(privateInvestAmount.value);
    notify.success('私募投資成功');
    privateInvestAmount.value = '';
  } catch (error) {
    console.error('私募投資失敗:', error);
    notify.error('投資失敗：' + (error.message || '未知錯誤'));
  } finally {
    isPrivateInvesting.value = false;
  }
};

// 處理 PCD 鑄造
const handleMintPCD = async () => {
  if (!mintToAddress.value) {
    notify.error('請輸入接收地址');
    return;
  }
  if (!mintAmount.value || Number(mintAmount.value) <= 0 || Number(mintAmount.value) > 20000) {
    notify.error('請輸入有效的鑄造數量（1-20000）');
    return;
  }

  try {
    isMinting.value = true;
    await mintPCD(mintToAddress.value, mintAmount.value);
    notify.success('PCD 鑄造成功');
    mintToAddress.value = '';
    mintAmount.value = '';
  } catch (error) {
    console.error('PCD 鑄造失敗:', error);
    notify.error('鑄造失敗：' + (error.message || '未知錯誤'));
  } finally {
    isMinting.value = false;
  }
};

// 頁面加載時獲取數據
onMounted(async () => {
  await checkUsdtAllowance();
});
</script>

<style scoped>
.pcd-management-root {
  padding: 20px;
  max-width: 1200px;
  margin: 0 auto;
}

.management-card {
  background: #0f1a0f;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
}

.card-title {
  color: #059669;
  font-size: 24px;
  margin-bottom: 24px;
  text-align: center;
}

.management-section {
  background: #152015;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
}

.section-header {
  margin-bottom: 16px;
}

.section-header h3 {
  color: #059669;
  font-size: 18px;
  margin: 0;
}

.section-content {
  color: #ffffff;
}

.info-row {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
}

.info-label {
  color: #059669;
  margin-right: 12px;
}

.info-value {
  font-size: 18px;
  font-weight: bold;
}

.input-row {
  display: flex;
  gap: 12px;
  align-items: center;
}

.count-input {
  flex: 1;
  background: #2a2a2a;
  border: 1px solid #059669;
  border-radius: 6px;
  color: #ffffff;
  padding: 8px 12px;
  font-size: 16px;
}

.count-input:focus {
  outline: none;
  border-color: #34d399;
}

.address-input {
  flex: 1;
  background: #2a2a2a;
  border: 1px solid #059669;
  border-radius: 6px;
  color: #ffffff;
  padding: 8px 12px;
  font-size: 16px;
  font-family: monospace;
}

.address-input:focus {
  outline: none;
  border-color: #34d399;
}

.update-btn {
  background: linear-gradient(90deg, #059669 0%, #34d399 100%);
  color: #152015;
  border: none;
  border-radius: 6px;
  padding: 8px 24px;
  font-size: 16px;
  cursor: pointer;
  transition: opacity 0.2s;
}

.update-btn:hover {
  opacity: 0.9;
}

.update-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.update-btn.danger {
  background: linear-gradient(90deg, #d9534f 0%, #c9302c 100%);
  color: #ffffff;
}

@media (max-width: 600px) {
  .pcd-management-root {
    padding: 12px;
  }
  
  .management-card {
    padding: 16px;
  }
  
  .input-row {
    flex-direction: column;
  }
  
  .update-btn {
    width: 100%;
  }
}
</style> 